import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import rootsData from './roots_data.json';
import { C } from './App';

// ─── Sarf Table ─────────────────────────────────────────────────────────────
const SarfTable = ({ section, darkMode, highlightForm }) => {
  const rows = section.rows || [];
  const highlightRef = useRef(null);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightForm]);

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr style={{ backgroundColor: darkMode ? 'rgba(49,94,73,0.08)' : 'rgba(49,94,73,0.04)' }}>
          <th
            colSpan={3}
            className="px-4 py-2.5 text-right font-arabic text-base font-semibold"
            style={{
              color: C.forest,
              borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
            }}
          >
            {section.label}
          </th>
        </tr>
        <tr style={{ backgroundColor: darkMode ? C.card2BgDark : '#f5f5f5' }}>
          {[['الضمير', 'right'], ['العدد', 'center'], ['الصيغة', 'right']].map(([h, align]) => (
            <th key={h} className={`px-4 py-1.5 text-${align} text-xs font-medium font-arabic`}
              style={{ borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`, color: darkMode ? C.mutedDark : C.mutedLight }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const isHighlighted = !!highlightForm && row.form === highlightForm;
          return (
            <tr key={i} ref={isHighlighted ? highlightRef : null} style={{
              backgroundColor: isHighlighted
                ? (darkMode ? 'rgba(49,94,73,0.28)' : 'rgba(49,94,73,0.13)')
                : (i % 2 === 0
                    ? (darkMode ? C.cardBgDark : '#ffffff')
                    : (darkMode ? C.card2BgDark : '#fafafa')),
              outline: isHighlighted ? `1px solid ${C.forest}40` : 'none',
            }}>
              <td className="px-4 py-2.5 font-arabic text-right text-sm" style={{ color: darkMode ? '#bbb' : '#333' }}>
                {row.person}
              </td>
              <td className="px-4 py-2.5 text-center text-xs" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
                {row.num === 'sg' ? 'مفرد' : row.num === 'du' ? 'مثنى' : 'جمع'}
              </td>
              <td className="px-4 py-2.5 font-arabic text-right text-lg font-semibold" style={{ color: C.forest }}>
                {row.form}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ─── Sarf Kabir Panel ────────────────────────────────────────────────────────
const SECTION_KEYS = ['madi', 'mudari_marfu', 'mudari_mansub', 'mudari_majzum', 'amr', 'nahy'];
const tabLabels = {
  madi: 'الماضي', mudari_marfu: 'مرفوع', mudari_mansub: 'منصوب',
  mudari_majzum: 'مجزوم', amr: 'الأمر', nahy: 'النهي',
};

function findFormLocation(sarf_kabir, form) {
  if (!form || !sarf_kabir) return null;
  const hasMaloomMajhool = sarf_kabir.maloom || sarf_kabir.majhool;
  if (hasMaloomMajhool) {
    for (const v of ['maloom', 'majhool']) {
      const sk = sarf_kabir[v] || {};
      for (const tab of SECTION_KEYS) {
        if ((sk[tab]?.rows || []).some(r => r.form === form)) return { voice: v, tab };
      }
    }
  } else {
    for (const tab of SECTION_KEYS) {
      if ((sarf_kabir[tab]?.rows || []).some(r => r.form === form)) return { voice: 'maloom', tab };
    }
  }
  return null;
}

const SarfKabirPanel = ({ sarf_kabir, darkMode, targetForm }) => {
  const found = findFormLocation(sarf_kabir, targetForm);
  const [activeTab, setActiveTab] = useState(found?.tab || 'madi');
  const [voice, setVoice] = useState(found?.voice || 'maloom');

  if (!sarf_kabir) {
    return (
      <div className="rounded-2xl p-10 text-center"
        style={{ border: `1px solid ${darkMode ? C.borderDark : C.borderLight}`, backgroundColor: darkMode ? C.cardBgDark : '#fff' }}>
        <p className="font-arabic text-3xl mb-2" style={{ color: darkMode ? '#333' : '#ddd' }}>قريباً</p>
        <p className="text-sm" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>Conjugation data not yet available.</p>
      </div>
    );
  }

  const hasMaloomMajhool = sarf_kabir.maloom || sarf_kabir.majhool;
  const sk = hasMaloomMajhool ? (sarf_kabir[voice] || {}) : sarf_kabir;
  const hasMajhool = hasMaloomMajhool && !!sarf_kabir.majhool;
  const voiceColor = voice === 'maloom' ? C.forest : C.orange;

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}>

      {/* Voice toggle */}
      {hasMajhool && (
        <div className="grid grid-cols-2"
          style={{ borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`, backgroundColor: darkMode ? C.card2BgDark : '#f5f5f5' }}>
          {[{ key: 'maloom', label: 'معلوم', color: C.forest }, { key: 'majhool', label: 'مجهول', color: C.orange }].map(v => (
            <button key={v.key} onClick={() => setVoice(v.key)}
              className="py-2.5 text-sm font-arabic font-medium transition-all focus:outline-none"
              style={voice === v.key
                ? { color: v.color, borderBottom: `2px solid ${v.color}`, backgroundColor: `${v.color}0a` }
                : { color: darkMode ? C.mutedDark : C.mutedLight, borderBottom: '2px solid transparent' }}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Section tabs */}
      <div className="flex overflow-x-auto"
        style={{ borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`, backgroundColor: darkMode ? C.cardBgDark : '#fff' }}>
        {SECTION_KEYS.map(key => sk[key] ? (
          <button key={key} onClick={() => setActiveTab(key)}
            className="px-4 py-2.5 text-sm font-arabic whitespace-nowrap transition-all focus:outline-none"
            style={activeTab === key
              ? { color: voiceColor, borderBottom: `2px solid ${voiceColor}`, backgroundColor: `${voiceColor}06` }
              : { color: darkMode ? C.mutedDark : C.mutedLight, borderBottom: '2px solid transparent' }}>
            {tabLabels[key]}
          </button>
        ) : null)}
      </div>

      <div style={{ backgroundColor: darkMode ? C.cardBgDark : '#fff' }}>
        {sk[activeTab] && <SarfTable section={sk[activeTab]} darkMode={darkMode} highlightForm={targetForm} />}
      </div>
    </div>
  );
};

// ─── Root Page ─────────────────────────────────────────────────────────────
export default function RootPage({ darkMode }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const targetForm = new URLSearchParams(location.search).get('form') || null;
  const rootData = rootsData[slug];
  const sarfRef = useRef(null);

  useEffect(() => {
    if (targetForm && sarfRef.current) {
      setTimeout(() => sarfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [targetForm]);

  const allBabs = React.useMemo(() => {
    if (!rootData) return [];
    return (rootData.babs || []).map((bab, i) => ({
      id: `bab-${i}`,
      label: bab.name || 'الباب',
      wazn_madi: bab.wazn_madi,
      wazn_mudari: bab.wazn_mudari,
      masdar: bab.masdar || null,
      masdar_meaning: bab.masdar_meaning || null,
      sarf_kabir: bab.sarf_kabir,
      type: bab.form_type || 'mujarrad',
    }));
  }, [rootData]);

  const [selectedBabId, setSelectedBabId] = useState(null);
  const selectedBab = allBabs.find(b => b.id === selectedBabId) || allBabs[0];

  const headerStyle = {
    backgroundColor: darkMode ? 'rgba(10,10,10,0.9)' : 'rgba(255,255,255,0.9)',
    borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
  };

  if (!rootData) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: darkMode ? C.pageBgDark : C.pageBgLight }}>
        <header className="fixed top-0 left-0 right-0 z-20 h-16 flex items-center px-5 gap-3 backdrop-blur-xl" style={headerStyle}>
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl focus:outline-none" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <h1 className="font-arabic text-xl font-bold" style={{ color: C.forest }}>{slug}</h1>
        </header>
        <main className="pt-24 pb-16 px-4 text-center">
          <p className="font-arabic text-5xl mb-4 pt-16" style={{ color: C.soft }}>؟</p>
          <p className="text-lg font-semibold mb-2" style={{ color: darkMode ? '#ccc' : '#222' }}>Root data not yet available</p>
          <p className="text-sm" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
            Data for <code className="font-mono px-1.5 py-0.5 rounded text-xs"
              style={{ backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0' }}>{slug}</code> is still being generated.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: darkMode ? C.pageBgDark : C.pageBgLight, color: darkMode ? C.textDark : C.textLight }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-20 h-16 flex items-center px-5 gap-4 backdrop-blur-xl" style={headerStyle}>
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl focus:outline-none" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="font-arabic text-2xl font-bold" style={{ color: C.forest }}>{rootData.root}</h1>
          <span className="text-sm truncate" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>— {rootData.meaning}</span>
        </div>
      </header>

      <main className="pt-24 pb-20 px-4 sm:px-8 max-w-3xl mx-auto">

        {/* Hero */}
        <div className="rounded-2xl p-8 mb-6 text-center relative overflow-hidden"
          style={{ backgroundColor: darkMode ? C.cardBgDark : '#fff', border: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}>
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ background: `radial-gradient(circle at 50% 0%, ${C.forest}, transparent 60%)` }} />
          <p className="font-arabic text-6xl font-bold mb-3 tracking-widest relative" style={{ color: C.forest }}>
            {rootData.root}
          </p>
          <p className="text-base relative" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
            {rootData.meaning}
          </p>
        </div>

        {/* Bab selector */}
        {allBabs.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
              Select Form
            </p>
            <div className="rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}>
              {allBabs.map((bab, idx) => {
                const isActive = selectedBab?.id === bab.id;
                const tc = bab.type === 'mujarrad' ? C.forest : C.orange;
                return (
                  <button key={bab.id} onClick={() => setSelectedBabId(bab.id)}
                    className="w-full text-right px-4 py-3 flex items-center justify-between gap-3 transition-all focus:outline-none"
                    style={{
                      backgroundColor: isActive ? `${tc}0d` : (darkMode ? C.cardBgDark : '#fff'),
                      borderBottom: idx < allBabs.length - 1 ? `1px solid ${darkMode ? C.borderDark : C.borderLight}` : 'none',
                    }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
                        style={{ color: tc, backgroundColor: `${tc}15` }}>
                        {bab.type === 'mujarrad' ? 'مجرد' : 'مزيد'}
                      </span>
                      <span className="text-sm truncate" style={{ color: darkMode ? '#aaa' : '#444' }}>
                        {bab.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0" dir="rtl">
                      {bab.masdar && (
                        <span className="font-arabic text-sm font-semibold" style={{ color: C.orange }}>{bab.masdar}</span>
                      )}
                      <span className="font-arabic text-base font-bold" style={{ color: tc }}>
                        {bab.wazn_madi}{bab.wazn_mudari ? ` / ${bab.wazn_mudari}` : ''}
                      </span>
                      {isActive && (
                        <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={tc} strokeWidth="2.5">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bab detail */}
        {selectedBab && (
          <div>
            <div className="rounded-2xl p-5 mb-5"
              style={{ backgroundColor: darkMode ? C.cardBgDark : '#fff', border: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-arabic text-2xl font-bold" style={{ color: selectedBab.type === 'mujarrad' ? C.forest : C.orange }}>
                    {selectedBab.wazn_madi}{selectedBab.wazn_mudari ? ` / ${selectedBab.wazn_mudari}` : ''}
                  </p>
                  <p className="text-sm mt-1" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>{selectedBab.label}</p>
                </div>
                {selectedBab.masdar && (
                  <div className="text-right">
                    <p className="font-arabic text-xl font-bold" style={{ color: C.orange }}>{selectedBab.masdar}</p>
                    {selectedBab.masdar_meaning && (
                      <p className="text-xs mt-0.5" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>{selectedBab.masdar_meaning}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div ref={sarfRef} className="flex items-center gap-2.5 mb-4">
              <div className="w-0.5 h-5 rounded-full" style={{ backgroundColor: C.forest }} />
              <h2 className="font-arabic text-lg font-bold" style={{ color: C.forest }}>الصرف الكبير</h2>
            </div>

            <SarfKabirPanel sarf_kabir={selectedBab.sarf_kabir} darkMode={darkMode} targetForm={targetForm} />
          </div>
        )}
      </main>
    </div>
  );
}
